<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import V2QrTablesPicker from '@/components/pages/qr/V2QrTablesPicker.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import {
    IMenuQrOrderingContext,
    resolveMenuQrDesign,
    TMenuQrDesign,
    TMenuQrDesignMode,
    TMenuQrTargetKind,
  } from '@/domain/menu-qr/menu-qr-design';
  import { TMenuQrPosterSize } from '@/domain/menu-qr/menu-qr-poster';
  import { IMenuQrSelection } from '@/domain/menu-qr/menu-qr-targets';
  import { ITable } from '@/interfaces/ITable';

  interface IDesignOption {
    id: TMenuQrDesignMode;
    title: string;
    hint: string;
  }

  interface ITargetRow {
    kind: TMenuQrTargetKind;
    checked: boolean;
    title: string;
    hint: string;
    design: TMenuQrDesign;
    badgeLabel: string;
  }

  const props = defineProps<{
    designMode: TMenuQrDesignMode;
    size: TMenuQrPosterSize;
    selection: IMenuQrSelection;
    effectiveTier: number;
    ordering: IMenuQrOrderingContext;
    /** Undefined while the branch's tables are loading. */
    tables?: ITable[];
    tablesLoading: boolean;
    tablesError: boolean;
    skippedTables: string[];
    languageName: string;
    /** False when the poster copy uses the UI locale instead of the menu language. */
    languageFromMenu: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:design-mode', mode: TMenuQrDesignMode): void;
    (e: 'update:size', size: TMenuQrPosterSize): void;
    (e: 'update:selection', selection: IMenuQrSelection): void;
    (e: 'retry-tables'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const DESIGN_MODES: TMenuQrDesignMode[] = ['view', 'auto', 'orders'];

  const designOptions = computed<IDesignOption[]>(() =>
    DESIGN_MODES.map((id) => ({
      id,
      title: i18nT(`qr.design.${id}`),
      hint: i18nT(`qr.design.${id}.hint`),
    }))
  );

  const sizeItems = computed(() => [
    { id: 'square', label: i18nT('qr.size.square') },
    { id: 'vertical', label: i18nT('qr.size.vertical') },
  ]);

  function badgeLabelFor(
    kind: TMenuQrTargetKind
  ): {
    design: TMenuQrDesign;
    badgeLabel: string;
  } {
    const design = resolveMenuQrDesign(props.designMode, kind, props.ordering);

    return {
      design,
      badgeLabel: i18nT(
        design === 'orders' ? 'qr.design.badge.orders' : 'qr.design.badge.view'
      ),
    };
  }

  const targetRows = computed<ITargetRow[]>(() => [
    {
      kind: 'storefront',
      checked: props.selection.includeStorefront,
      title: i18nT('qr.targets.storefront'),
      hint: i18nT('qr.targets.storefront.hint'),
      ...badgeLabelFor('storefront'),
    },
    {
      kind: 'takeaway',
      checked: props.selection.includeTakeAway,
      title: i18nT('qr.targets.takeAway'),
      hint: i18nT('qr.targets.takeAway.hint'),
      ...badgeLabelFor('takeaway'),
    },
    {
      kind: 'table',
      checked: props.selection.includeTables,
      title: i18nT('qr.targets.tables'),
      hint: i18nT('qr.targets.tables.hint'),
      ...badgeLabelFor('table'),
    },
  ]);

  function updateSelection(partial: Partial<IMenuQrSelection>) {
    emit('update:selection', { ...props.selection, ...partial });
  }

  function onTargetChange(kind: TMenuQrTargetKind, event: Event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    if (kind === 'storefront') {
      updateSelection({ includeStorefront: input.checked });
    } else if (kind === 'takeaway') {
      updateSelection({ includeTakeAway: input.checked });
    } else {
      updateSelection({ includeTables: input.checked });
    }
  }

  function onSizeChange(id: string) {
    emit('update:size', id === 'vertical' ? 'vertical' : 'square');
  }
</script>

<template>
  <div class="qr-options">
    <section class="options-section">
      <h3 class="section-label">{{ $t('qr.design.label') }}</h3>
      <div class="design-options">
        <label
          v-for="option in designOptions"
          :key="option.id"
          class="design-option"
          :class="{ 'is-selected': option.id === designMode }"
        >
          <input
            type="radio"
            class="design-radio"
            name="qr-design-mode"
            :value="option.id"
            :checked="option.id === designMode"
            @change="emit('update:design-mode', option.id)"
          />
          <span class="design-option-text">
            <span class="design-option-title">{{ option.title }}</span>
            <span class="design-option-hint">{{ option.hint }}</span>
          </span>
        </label>
      </div>
      <p v-if="languageName && designMode !== 'view'" class="section-note">
        {{
          $t(
            languageFromMenu
              ? 'qr.design.language'
              : 'qr.design.languageFallback',
            { language: languageName }
          )
        }}
      </p>
    </section>

    <section class="options-section">
      <h3 class="section-label">{{ $t('qr.size.label') }}</h3>
      <PosUiSegmented :value="size" :items="sizeItems" @input="onSizeChange" />
    </section>

    <section class="options-section">
      <h3 class="section-label">{{ $t('qr.targets.label') }}</h3>

      <template v-if="effectiveTier === 0">
        <label class="target-row is-disabled">
          <input type="checkbox" class="target-checkbox" checked disabled />
          <span class="target-text">
            <span class="target-title">{{ $t('qr.targets.storefront') }}</span>
            <span class="target-hint">{{
              $t('qr.targets.storefront.hint')
            }}</span>
          </span>
        </label>
        <p class="section-note">{{ $t('qr.targets.tierNote') }}</p>
      </template>

      <template v-else>
        <label v-for="row in targetRows" :key="row.kind" class="target-row">
          <input
            type="checkbox"
            class="target-checkbox"
            :checked="row.checked"
            @change="onTargetChange(row.kind, $event)"
          />
          <span class="target-text">
            <span class="target-title">{{ row.title }}</span>
            <span class="target-hint">{{ row.hint }}</span>
          </span>
          <PosUiBadge
            class="target-badge"
            :label="row.badgeLabel"
            :color="row.design === 'orders' ? 'info' : 'neutral'"
            size="xs"
          />
        </label>

        <V2QrTablesPicker
          v-if="selection.includeTables"
          class="tables-picker-slot"
          :tables="tables"
          :selected-ids="selection.selectedTableIds"
          :loading="tablesLoading"
          :load-error="tablesError"
          @update:selected-ids="updateSelection({ selectedTableIds: $event })"
          @retry="emit('retry-tables')"
        />

        <p v-if="skippedTables.length > 0" class="section-warning">
          {{
            $t('qr.targets.tablesWithoutToken', { count: skippedTables.length })
          }}
        </p>
      </template>
    </section>
  </div>
</template>

<style scoped lang="scss">
  .qr-options {
    @apply tw-flex tw-flex-col tw-gap-6;

    .options-section {
      @apply tw-flex tw-flex-col tw-gap-3;

      .section-label {
        @apply tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .section-note {
        @apply tw-m-0 tw-text-xs tw-text-neutral-500;
      }

      .section-warning {
        @apply tw-m-0 tw-rounded-lg tw-border tw-border-solid tw-border-amber-300 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-800;
      }

      .design-options {
        @apply tw-flex tw-flex-col tw-gap-2;

        .design-option {
          @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3 tw-transition-colors;

          &:hover {
            @apply tw-bg-neutral-50;
          }

          &.is-selected {
            @apply tw-border-orange-600 tw-bg-orange-50;
          }

          .design-radio {
            @apply tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer tw-accent-orange-600;
          }

          .design-option-text {
            @apply tw-flex tw-flex-col tw-gap-0.5;

            .design-option-title {
              @apply tw-text-sm tw-font-medium tw-text-neutral-900;
            }

            .design-option-hint {
              @apply tw-text-xs tw-text-neutral-500;
            }
          }
        }
      }

      .target-row {
        @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-px-1 tw-py-1;

        &.is-disabled {
          @apply tw-cursor-default tw-opacity-70;
        }

        .target-checkbox {
          @apply tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer tw-accent-orange-600;
        }

        .target-text {
          @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;

          .target-title {
            @apply tw-text-sm tw-font-medium tw-text-neutral-900;
          }

          .target-hint {
            @apply tw-text-xs tw-text-neutral-500;
          }
        }

        .target-badge {
          @apply tw-shrink-0;
        }
      }

      .tables-picker-slot {
        @apply tw-mt-1 tw-pl-8;
      }
    }
  }
</style>
